import {
  ApiError,
  type Environment,
  type Incident,
  type IncidentSeverity,
  type IncidentStatus,
  type SetNextUpgradeRequest,
} from '@central/api';
import { Card, InfoRow, StatusChip, formatAgo, formatDateTime, type StatusTone } from '@central/ui';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Table,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, LoadMore, QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { fromDateTimeInput, toDateTimeInput } from '../lib/format';
import { mono, muted } from '../lib/labels';
import { useCanWrite } from '../lib/me';

/** Impact as customers see it: minor is Degraded, major and critical are Down. */
const severityOptions: Array<{ value: IncidentSeverity; label: string }> = [
  { value: 'minor', label: 'Degraded (minor)' },
  { value: 'major', label: 'Down (major)' },
  { value: 'critical', label: 'Down (critical)' },
];

const statusChip: Record<IncidentStatus, { tone: StatusTone; label: string }> = {
  investigating: { tone: 'warn', label: 'Investigating' },
  identified: { tone: 'warn', label: 'Identified' },
  monitoring: { tone: 'unk', label: 'Monitoring' },
  resolved: { tone: 'ok', label: 'Resolved' },
};

const statusOptions = (Object.keys(statusChip) as IncidentStatus[]).map((s) => ({
  value: s,
  label: statusChip[s].label,
}));

function Impact({ severity }: { severity: IncidentSeverity }) {
  return severity === 'minor' ? (
    <StatusChip tone="warn">Degraded</StatusChip>
  ) : (
    <StatusChip tone="down">Down</StatusChip>
  );
}

/** "Oct 9, 02:14 – 02:37", or "since Oct 21, 09:40" while open. */
function windowText(n: Pick<Incident, 'startedAt' | 'endedAt'>): string {
  if (!n.endedAt) return `since ${formatDateTime(n.startedAt)}`;
  const [startDay] = formatDateTime(n.startedAt).split(', ');
  const [endDay, endTime] = formatDateTime(n.endedAt).split(', ');
  return `${formatDateTime(n.startedAt)} – ${endDay === startDay ? endTime : `${endDay}, ${endTime}`}`;
}

/**
 * The environment's incidents, newest first. Customers on it see them on
 * Deployment detail and Home while it shows incidents; staff post and update
 * their own, and automatic ones follow the core services.
 */
export function Incidents({ e }: { e: Environment }) {
  const api = useApi();
  const canWrite = useCanWrite();
  const list = useInfiniteQuery({
    queryKey: keys.environmentIncidents(e.id),
    queryFn: ({ pageParam }) => api.environments.incidents.list(e.id, { cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const [posting, setPosting] = useState(false);
  const [updating, setUpdating] = useState<Incident>();
  const [editing, setEditing] = useState<Incident>();
  const [deleting, setDeleting] = useState<Incident>();
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card
      title="Incidents"
      padded={false}
      action={
        canWrite && (
          <Button variant="default" h={36} onClick={() => setPosting(true)}>
            Post incident
          </Button>
        )
      }
    >
      {!e.effectiveVisibility.incidents && (
        <div style={{ padding: '0 20px 12px', fontSize: 13, ...muted }}>
          Customers don’t see incidents on this environment: its visibility hides them.
        </div>
      )}
      <QueryState isPending={list.isPending} error={list.error}>
        {() =>
          items.length === 0 ? (
            <Empty>No incidents yet.</Empty>
          ) : (
            <DataTable
              label="Incidents"
              head={['Incident', 'Impact', 'Status', 'Window', 'Next update', '']}
              minWidth={760}
            >
              {items.map((n) => (
                <Table.Tr key={n.id}>
                  <Table.Td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontWeight: 500 }}>{n.title}</span>
                      <span style={{ fontSize: 13, ...muted }}>
                        {[
                          n.auto && 'automatic',
                          n.planned && 'planned',
                          n.updates.length > 0 &&
                            `${n.updates.length} ${n.updates.length === 1 ? 'update' : 'updates'}, last ${formatAgo(n.updates.at(-1)!.at)}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <Impact severity={n.severity} />
                  </Table.Td>
                  <Table.Td>
                    {n.status ? (
                      <StatusChip tone={statusChip[n.status].tone}>
                        {statusChip[n.status].label}
                      </StatusChip>
                    ) : n.endedAt ? (
                      <StatusChip tone="ok">Recovered</StatusChip>
                    ) : (
                      <StatusChip tone="down">Ongoing</StatusChip>
                    )}
                  </Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{windowText(n)}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    {n.nextUpdateBy ? formatDateTime(n.nextUpdateBy) : '–'}
                  </Table.Td>
                  <Table.Td>
                    {canWrite && !n.auto && (
                      <Group gap={6} wrap="nowrap" justify="flex-end">
                        <Button size="xs" variant="default" onClick={() => setUpdating(n)}>
                          Post update
                        </Button>
                        <Button size="xs" variant="subtle" onClick={() => setEditing(n)}>
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(n)}
                        >
                          Delete
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
      {list.hasNextPage && (
        <LoadMore
          what="incidents"
          loading={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        />
      )}
      {posting && <PostIncidentModal e={e} onClose={() => setPosting(false)} />}
      {updating && <PostUpdateModal e={e} n={updating} onClose={() => setUpdating(undefined)} />}
      {editing && <EditIncidentModal e={e} n={editing} onClose={() => setEditing(undefined)} />}
      {deleting && (
        <DeleteIncidentModal e={e} n={deleting} onClose={() => setDeleting(undefined)} />
      )}
    </Card>
  );
}

function useRefreshIncidents(envId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: keys.environmentIncidents(envId) });
}

/** Field errors from a 400 response, by request key. */
function fieldErrors(err: unknown): Record<string, string> {
  return (err instanceof ApiError && err.fields) || {};
}

// Each modal is mounted only while open, so it starts from the latest stored
// incident and every way out drops unsaved edits.

function PostIncidentModal({ e, onClose }: { e: Environment; onClose: () => void }) {
  const api = useApi();
  const refresh = useRefreshIncidents(e.id);
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('minor');
  const [status, setStatus] = useState<IncidentStatus>('investigating');
  const [planned, setPlanned] = useState(false);
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [nextUpdateBy, setNextUpdateBy] = useState('');
  const [body, setBody] = useState('');
  const [titleError, setTitleError] = useState<string>();
  const post = useMutation({
    mutationFn: () =>
      api.environments.incidents.create(e.id, {
        title: title.trim(),
        severity,
        status,
        planned: planned || undefined,
        startedAt: startedAt ? fromDateTimeInput(startedAt) : undefined,
        endedAt: status === 'resolved' && endedAt ? fromDateTimeInput(endedAt) : undefined,
        nextUpdateBy:
          status !== 'resolved' && nextUpdateBy ? fromDateTimeInput(nextUpdateBy) : undefined,
        body: body.trim() || undefined,
      }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const fields = fieldErrors(post.error);
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!title.trim()) return setTitleError('Name the incident in customer wording.');
    setTitleError(undefined);
    post.mutate();
  };
  return (
    <Modal opened onClose={onClose} title={`Post an incident on ${e.name}`} size="lg">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Title"
          description="Customers see this wording."
          data-autofocus
          value={title}
          error={titleError ?? fields.title}
          onChange={(ev) => setTitle(ev.currentTarget.value)}
        />
        <Group grow align="flex-start">
          <Select
            label="Impact"
            data={severityOptions}
            value={severity}
            allowDeselect={false}
            error={fields.severity}
            onChange={(v) => v && setSeverity(v as IncidentSeverity)}
          />
          <Select
            label="Status"
            data={statusOptions}
            value={status}
            allowDeselect={false}
            error={fields.status}
            onChange={(v) => v && setStatus(v as IncidentStatus)}
          />
        </Group>
        <Checkbox
          label="Planned maintenance"
          checked={planned}
          onChange={(ev) => setPlanned(ev.currentTarget.checked)}
        />
        <Group grow align="flex-start">
          <TextInput
            label="Started (UTC)"
            description="Empty is now."
            type="datetime-local"
            value={startedAt}
            error={fields.startedAt}
            onChange={(ev) => setStartedAt(ev.currentTarget.value)}
          />
          {status === 'resolved' ? (
            <TextInput
              label="Ended (UTC)"
              description="Empty is now."
              type="datetime-local"
              value={endedAt}
              error={fields.endedAt}
              onChange={(ev) => setEndedAt(ev.currentTarget.value)}
            />
          ) : (
            <TextInput
              label="Next update by (UTC)"
              description="Optional."
              type="datetime-local"
              value={nextUpdateBy}
              error={fields.nextUpdateBy}
              onChange={(ev) => setNextUpdateBy(ev.currentTarget.value)}
            />
          )}
        </Group>
        <Textarea
          label="First update"
          description="Optional. Customers see it under the incident."
          autosize
          minRows={3}
          value={body}
          error={fields.body}
          onChange={(ev) => setBody(ev.currentTarget.value)}
        />
        {post.error && !Object.keys(fields).length && (
          <Alert color="red">{errorMessage(post.error)}</Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={post.isPending}>
            Post incident
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

function PostUpdateModal({ e, n, onClose }: { e: Environment; n: Incident; onClose: () => void }) {
  const api = useApi();
  const refresh = useRefreshIncidents(e.id);
  const [status, setStatus] = useState<IncidentStatus>(n.status ?? 'investigating');
  const [body, setBody] = useState('');
  const [nextUpdateBy, setNextUpdateBy] = useState('');
  const [bodyError, setBodyError] = useState<string>();
  const post = useMutation({
    mutationFn: () =>
      api.environments.incidents.postUpdate(e.id, n.id, {
        status,
        body: body.trim(),
        nextUpdateBy:
          status !== 'resolved' && nextUpdateBy ? fromDateTimeInput(nextUpdateBy) : undefined,
      }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const fields = fieldErrors(post.error);
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!body.trim()) return setBodyError('Say what changed.');
    setBodyError(undefined);
    post.mutate();
  };
  return (
    <Modal opened onClose={onClose} title={n.title} size="lg">
      {n.updates.length > 0 && (
        <ol
          aria-label="Updates"
          style={{ listStyle: 'none', margin: '0 0 16px', padding: 0, fontSize: 14 }}
        >
          {n.updates.map((u, i) => (
            <li key={i} style={{ padding: '6px 0' }}>
              <span style={{ fontSize: 13, ...muted }}>
                {formatDateTime(u.at)} · {statusChip[u.status].label}
              </span>
              <div>{u.body}</div>
            </li>
          ))}
        </ol>
      )}
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <Group grow align="flex-start">
          <Select
            label="Status"
            data={statusOptions}
            value={status}
            allowDeselect={false}
            error={fields.status}
            onChange={(v) => v && setStatus(v as IncidentStatus)}
          />
          <TextInput
            label="Next update by (UTC)"
            description={status === 'resolved' ? 'None once resolved.' : 'Empty clears it.'}
            type="datetime-local"
            disabled={status === 'resolved'}
            value={status === 'resolved' ? '' : nextUpdateBy}
            error={fields.nextUpdateBy}
            onChange={(ev) => setNextUpdateBy(ev.currentTarget.value)}
          />
        </Group>
        <Textarea
          label="Update"
          description="Customers see it under the incident."
          data-autofocus
          autosize
          minRows={3}
          value={body}
          error={bodyError ?? fields.body}
          onChange={(ev) => setBody(ev.currentTarget.value)}
        />
        {post.error && !Object.keys(fields).length && (
          <Alert color="red">{errorMessage(post.error)}</Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={post.isPending}>
            Post update
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

function EditIncidentModal({
  e,
  n,
  onClose,
}: {
  e: Environment;
  n: Incident;
  onClose: () => void;
}) {
  const api = useApi();
  const refresh = useRefreshIncidents(e.id);
  const [title, setTitle] = useState(n.title);
  const [severity, setSeverity] = useState<IncidentSeverity>(n.severity);
  const [planned, setPlanned] = useState(n.planned);
  const [startedAt, setStartedAt] = useState(toDateTimeInput(n.startedAt));
  const [endedAt, setEndedAt] = useState(n.endedAt ? toDateTimeInput(n.endedAt) : '');
  const [titleError, setTitleError] = useState<string>();
  const save = useMutation({
    mutationFn: () =>
      api.environments.incidents.update(e.id, n.id, {
        title: title.trim() !== n.title ? title.trim() : undefined,
        severity: severity !== n.severity ? severity : undefined,
        planned: planned !== n.planned ? planned : undefined,
        startedAt:
          startedAt !== toDateTimeInput(n.startedAt) ? fromDateTimeInput(startedAt) : undefined,
        endedAt:
          n.endedAt && endedAt && endedAt !== toDateTimeInput(n.endedAt)
            ? fromDateTimeInput(endedAt)
            : undefined,
      }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const fields = fieldErrors(save.error);
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!title.trim()) return setTitleError('Name the incident in customer wording.');
    setTitleError(undefined);
    save.mutate();
  };
  return (
    <Modal opened onClose={onClose} title="Edit incident" size="lg">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Title"
          data-autofocus
          value={title}
          error={titleError ?? fields.title}
          onChange={(ev) => setTitle(ev.currentTarget.value)}
        />
        <Select
          label="Impact"
          data={severityOptions}
          value={severity}
          allowDeselect={false}
          error={fields.severity}
          onChange={(v) => v && setSeverity(v as IncidentSeverity)}
        />
        <Checkbox
          label="Planned maintenance"
          checked={planned}
          onChange={(ev) => setPlanned(ev.currentTarget.checked)}
        />
        <Group grow align="flex-start">
          <TextInput
            label="Started (UTC)"
            type="datetime-local"
            value={startedAt}
            error={fields.startedAt}
            onChange={(ev) => setStartedAt(ev.currentTarget.value)}
          />
          {n.endedAt && (
            <TextInput
              label="Ended (UTC)"
              type="datetime-local"
              value={endedAt}
              error={fields.endedAt}
              onChange={(ev) => setEndedAt(ev.currentTarget.value)}
            />
          )}
        </Group>
        {save.error && !Object.keys(fields).length && (
          <Alert color="red">{errorMessage(save.error)}</Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={save.isPending}>
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

function DeleteIncidentModal({
  e,
  n,
  onClose,
}: {
  e: Environment;
  n: Incident;
  onClose: () => void;
}) {
  const api = useApi();
  const refresh = useRefreshIncidents(e.id);
  const remove = useMutation({
    mutationFn: () => api.environments.incidents.remove(e.id, n.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Modal opened onClose={onClose} title="Delete incident">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <span style={{ fontSize: 14 }}>
          “{n.title}” and its updates disappear from the portal. Use this for an incident posted in
          error; resolve a real one instead.
        </span>
        {remove.error && <Alert color="red">{errorMessage(remove.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
            Delete
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/**
 * The upgrade Rethink scheduled. Customers on a shared environment see
 * "Next upgrade · Rethink-scheduled"; on a dedicated one, the planned upgrade
 * in its Version card.
 */
export function NextUpgrade({ e }: { e: Environment }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const [editing, setEditing] = useState(false);
  const clear = useMutation({
    mutationFn: () => api.environments.clearNextUpgrade(e.id),
    onSuccess: (next) => queryClient.setQueryData(keys.environment(e.id), next),
  });
  const up = e.nextUpgrade;
  return (
    <Card
      title="Next upgrade"
      action={
        canWrite && (
          <Group gap={8}>
            {up && (
              <Button
                variant="subtle"
                color="red"
                h={36}
                loading={clear.isPending}
                onClick={() => clear.mutate()}
              >
                Clear
              </Button>
            )}
            <Button variant="default" h={36} onClick={() => setEditing(true)}>
              {up ? 'Change' : 'Schedule upgrade'}
            </Button>
          </Group>
        )
      }
    >
      {up ? (
        <>
          <InfoRow label="Version">
            <span style={mono}>{up.version}</span>
          </InfoRow>
          <InfoRow label="Window (UTC)">
            {windowText({ startedAt: up.startsAt, endedAt: up.endsAt })}
          </InfoRow>
        </>
      ) : (
        <span style={{ fontSize: 14, ...muted }}>None scheduled.</span>
      )}
      {clear.error && <Alert color="red">{errorMessage(clear.error)}</Alert>}
      {editing && <NextUpgradeModal e={e} onClose={() => setEditing(false)} />}
    </Card>
  );
}

function NextUpgradeModal({ e, onClose }: { e: Environment; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const up = e.nextUpgrade;
  const [version, setVersion] = useState(up?.version ?? '');
  const [startsAt, setStartsAt] = useState(up ? toDateTimeInput(up.startsAt) : '');
  const [endsAt, setEndsAt] = useState(up ? toDateTimeInput(up.endsAt) : '');
  const [problems, setProblems] = useState<Partial<Record<keyof SetNextUpgradeRequest, string>>>(
    {},
  );
  const save = useMutation({
    mutationFn: () =>
      api.environments.setNextUpgrade(e.id, {
        version: version.trim(),
        startsAt: fromDateTimeInput(startsAt),
        endsAt: fromDateTimeInput(endsAt),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(keys.environment(e.id), next);
      onClose();
    },
  });
  const fields = fieldErrors(save.error);
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    const p: typeof problems = {};
    if (!version.trim()) p.version = 'Name the version.';
    if (!startsAt) p.startsAt = 'Pick when the window opens.';
    if (!endsAt) p.endsAt = 'Pick when it closes.';
    else if (startsAt && endsAt <= startsAt) p.endsAt = 'Close the window after it opens.';
    setProblems(p);
    if (Object.keys(p).length === 0) save.mutate();
  };
  return (
    <Modal opened onClose={onClose} title={`Schedule the next upgrade of ${e.name}`}>
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Version"
          placeholder="1.1.0"
          data-autofocus
          value={version}
          error={problems.version ?? fields.version}
          onChange={(ev) => setVersion(ev.currentTarget.value)}
        />
        <Group grow align="flex-start">
          <TextInput
            label="Starts (UTC)"
            type="datetime-local"
            value={startsAt}
            error={problems.startsAt ?? fields.startsAt}
            onChange={(ev) => setStartsAt(ev.currentTarget.value)}
          />
          <TextInput
            label="Ends (UTC)"
            type="datetime-local"
            value={endsAt}
            error={problems.endsAt ?? fields.endsAt}
            onChange={(ev) => setEndsAt(ev.currentTarget.value)}
          />
        </Group>
        {save.error && !Object.keys(fields).length && (
          <Alert color="red">{errorMessage(save.error)}</Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={save.isPending}>
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
